import { useState, useEffect, useRef, useCallback } from "react";
import { useAuthActions } from "@convex-dev/auth/react";

export type AuthMode = "signIn" | "signUp";

/** Provider id registered in `convex/auth.ts`. */
const PROVIDER = "email-code";
const CODE_LENGTH = 6;
/** Seconds before "Resend code" becomes available again. */
const RESEND_COOLDOWN_S = 30;

/** The address the backend keys accounts on — canonical, so we send it that way. */
function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Passwordless sign-in. The user gives us an email, we mail a one-time code,
 * they type it back. Sign-up and sign-in are the same act — `mode` only picks
 * the copy, so the marketing CTAs keep their intent.
 */
export function AuthPanel({
  initialMode,
  onClose,
}: {
  initialMode: AuthMode;
  onClose: () => void;
}) {
  const { signIn } = useAuthActions();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (step === "email" ? emailRef : codeRef).current?.focus();
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const sendCode = useCallback(
    async (address: string, resend: boolean) => {
      setError(null);
      setNotice(null);
      setBusy(true);
      try {
        await signIn(PROVIDER, { email: address });
        setSentTo(address);
        setStep("code");
        setCode("");
        setCooldown(RESEND_COOLDOWN_S);
        if (resend) setNotice("Sent. Check your inbox again.");
      } catch (err) {
        setError(messageFor(err, "send"));
        console.error(err);
      } finally {
        setBusy(false);
      }
    },
    [signIn],
  );

  const submitCode = useCallback(
    async (value: string) => {
      setError(null);
      setNotice(null);
      setBusy(true);
      try {
        await signIn(PROVIDER, { email: sentTo, code: value });
        // On success ConvexAuthProvider flips to authenticated and App swaps in
        // the shell; closing keeps things tidy if the modal is still mounted.
        onClose();
      } catch (err) {
        setError(messageFor(err, "verify"));
        setCode("");
        codeRef.current?.focus();
        console.error(err);
      } finally {
        setBusy(false);
      }
    },
    [signIn, sentTo, onClose],
  );

  function onEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    const address = normalizeEmail(email);
    if (address.length === 0) return;
    void sendCode(address, false);
  }

  function onCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length === CODE_LENGTH) void submitCode(code);
  }

  function onCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    // Typing (or pasting) the last digit is the submit gesture.
    if (digits.length === CODE_LENGTH && !busy) void submitCode(digits);
  }

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in to Slackcord"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card auth-card">
        <button className="close-x" onClick={onClose} aria-label="Close">
          ×
        </button>

        {step === "email" ? (
          <>
            <h2>
              {initialMode === "signUp" ? "Create your account" : "Welcome back"}
            </h2>
            <p className="muted" style={{ fontSize: "0.94rem", marginTop: 6 }}>
              Enter your work email and we'll send you a one-time code. No
              password to remember.
            </p>

            <form className="form" onSubmit={onEmailSubmit}>
              <input
                ref={emailRef}
                className="input"
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@company.com"
                aria-label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {error && <p className="form-error">{error}</p>}
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {busy ? "Sending…" : "Email me a code"}
              </button>
            </form>
            <p className="form-hint" style={{ marginTop: 16 }}>
              New to Slackcord? The same code creates your account.
            </p>
          </>
        ) : (
          <>
            <h2>Check your email</h2>
            <p className="muted" style={{ fontSize: "0.94rem", marginTop: 6 }}>
              We sent a {CODE_LENGTH}-digit code to{" "}
              <strong className="auth-sent-to">{sentTo}</strong>. It expires in
              10 minutes.
            </p>

            <form className="form" onSubmit={onCodeSubmit}>
              <input
                ref={codeRef}
                className="input auth-code-input"
                type="text"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d*"
                maxLength={CODE_LENGTH}
                placeholder="000000"
                aria-label={`${CODE_LENGTH}-digit sign-in code`}
                value={code}
                onChange={(e) => onCodeChange(e.target.value)}
                required
              />
              {error && <p className="form-error">{error}</p>}
              {notice && !error && <p className="form-notice">{notice}</p>}
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || code.length < CODE_LENGTH}
              >
                {busy ? "Verifying…" : "Sign in"}
              </button>
            </form>

            <div className="auth-alt">
              <button
                type="button"
                className="link-btn"
                disabled={busy || cooldown > 0}
                onClick={() => void sendCode(sentTo, true)}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
              <button
                type="button"
                className="link-btn"
                disabled={busy}
                onClick={() => {
                  setStep("email");
                  setError(null);
                  setNotice(null);
                  setCode("");
                }}
              >
                Use a different email
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Server errors reach the client as a stringified Convex error. Surface the
 * cases the user can act on; stay vague about everything else so a failed
 * verification never reveals whether the address has an account.
 */
function messageFor(err: unknown, phase: "send" | "verify"): string {
  const text = err instanceof Error ? err.message : String(err);
  if (/Too many codes requested/i.test(text)) {
    return "Too many codes requested for this email. Wait a few minutes and try again.";
  }
  if (/Email is not configured/i.test(text)) {
    return "We can't send codes right now. Please try again shortly.";
  }
  if (/permanently bounced/i.test(text)) {
    return "That address bounced. Check the spelling and try again.";
  }
  if (phase === "send") {
    return "We couldn't send the code. Check the address and try again.";
  }
  return "That code isn't right, or it expired. Request a new one.";
}
